import {
  getSubscription,
  putService as putServiceEndpoint,
  postService as postServiceEndpoint,
  deleteService as deleteServiceEndpoint,
  deleteItem as deleteItemEndpoint,
  putItem,
  ItemsBulkPayload,
  postBulkItems,
  postItem,
  postBulkServices,
  ServicesBulkPayload,
} from '@front/common/endpoints'
import { Item, Service, ServiceType, Subscription } from '@front/common/types'
import {
  createUpsellService,
  getFlattenItems,
  getItemById,
  getPublicNamespaceName,
  getServicesByType,
  isCustomFood,
} from '@front/common/utils'
import { computed } from '@vue/composition-api'
import { useStore } from '@vueblocks/vue-use-vuex'

import useApiState from '@member-area/core/hooks/useApiState'
import useAuth from '@member-area/middleware/auth.hook'

import useCustomer from '../Customer/Customer.hook'
import useOrder from '../Order/Order.hook'

import {
  INSERT_SUBSCRIPTIONS,
  UPSERT_SERVICE,
  DELETE_SERVICE,
  UPSERT_ITEM,
  DELETE_ITEM,
} from './constants'

const useSubscription = () => {
  const store = useStore()
  const { fetchCustomer } = useCustomer()
  const { fetchOrders } = useOrder()
  const { auth } = useAuth()

  const [
    _onFetchSubscriptionSuccess,
    fetchSubscriptionState,
    fetchSubscriptionAction,
  ]: any[] = useApiState(getSubscription)
  const [onAddServiceSuccess, addServiceState, postService]: any[] =
    useApiState(postServiceEndpoint)
  const [onDeleteServiceSuccess, deleteServiceState, deleteService]: any[] =
    useApiState(deleteServiceEndpoint)
  const [_onUpdateServiceSuccess, updateServiceState, putServiceAction]: any[] =
    useApiState(putServiceEndpoint)

  const [_updateItemCallback, updateItemState, putItemAction]: any[] =
    useApiState(putItem)
  const [
    _postBultItemsCallback,
    postBulkItemsState,
    postBulkItemsAction,
  ]: any[] = useApiState(postBulkItems)
  const [_deleteItemCallback, deleteItemState, deleteItemAction]: any[] =
    useApiState(deleteItemEndpoint)
  const [_postItemCallback, postItemState, postItemAction]: any[] =
    useApiState(postItem)

  const subscription = computed<Subscription>(() => {
    return store.getters['subscription/get']
  })

  const upsellService = computed(() => {
    const [service] = getServicesByType(
      subscription.value.services,
      ServiceType.upsell,
    )

    return service
  })

  async function fetchSubscription() {
    const data = await fetchSubscriptionAction(auth.value?.id)

    store.commit(getPublicNamespaceName(INSERT_SUBSCRIPTIONS), data)
  }

  onAddServiceSuccess(async (payload: Service) => {
    store.commit(getPublicNamespaceName(UPSERT_SERVICE), payload)

    fetchCustomer()
    fetchSubscription()
    fetchOrders()
  })

  onDeleteServiceSuccess((_, { data }) => {
    const [id] = data

    store.commit(getPublicNamespaceName(DELETE_SERVICE), id)

    fetchCustomer()
    fetchSubscription()
    fetchOrders()
  })

  async function putService(service: Service) {
    const data = await putServiceAction(service)

    store.commit(getPublicNamespaceName(UPSERT_SERVICE), data)

    await fetchSubscription()
    await fetchOrders()
  }

  async function updateServices(payload: ServicesBulkPayload) {
    await postBulkServices(payload, subscription.value.id)
    await fetchSubscription()
    await fetchOrders()
  }

  async function updateItems(payload: ItemsBulkPayload) {
    if (!upsellService.value) {
      const data = await postService(
        createUpsellService(payload.create, undefined, subscription.value.id),
      )

      store.commit(getPublicNamespaceName(UPSERT_SERVICE), data)
      fetchCustomer()
      fetchOrders()
      return
    }

    await postBulkItemsAction(
      payload,
      upsellService.value.id,
      subscription.value.id,
    )

    payload.create.concat(payload.update).forEach((item) => {
      store.commit(getPublicNamespaceName(UPSERT_ITEM), item)
    })
    payload.delete.forEach((itemId) => {
      store.commit(getPublicNamespaceName(DELETE_ITEM), itemId)
    })

    fetchCustomer()
    fetchOrders()
  }

  async function createItem(item: Item) {
    if (!upsellService.value) {
      const data = await postService(
        createUpsellService([item], undefined, subscription.value.id),
      )

      store.commit(getPublicNamespaceName(UPSERT_SERVICE), data)
      fetchCustomer()
      fetchOrders()
      return
    }

    const data = await postItemAction(item, subscription.value.id)

    store.commit(getPublicNamespaceName(UPSERT_ITEM), data)
    fetchCustomer()
    fetchOrders()
  }

  async function updateItem(item: Item) {
    const data = await putItemAction(item, subscription.value.id)

    store.commit(getPublicNamespaceName(UPSERT_ITEM), data)
    fetchCustomer()

    if (isCustomFood(item.type)) {
      fetchSubscription()
    }
    fetchOrders()
  }

  async function deleteItem(itemId: Item['id']) {
    const item = getItemById(
      getFlattenItems(subscription.value.services),
      itemId,
    )

    await deleteItemAction(item, subscription.value.id)

    store.commit(getPublicNamespaceName(DELETE_ITEM), item.id)
    fetchCustomer()

    if (isCustomFood(item.type)) {
      fetchSubscription()
    }
    fetchOrders()
  }

  return {
    subscription,
    fetchSubscriptionState,
    addServiceState,
    deleteServiceState,
    updateServiceState,
    fetchSubscription,
    postService,
    deleteService,
    putService,
    updateItemState,
    createItem,
    postItemState,
    updateItem,
    deleteItem,
    deleteItemState,
    updateItems,
    postBulkItemsState,
    upsellService,
    updateServices,
  }
}

export default useSubscription
